import { useLayoutEffect, useRef, type ComponentType } from 'react'

import { BrowserRouter, Route, Routes } from 'react-router-dom'

import { type SizePassportsProps } from './components/CustomerHubCore'
import { type Role } from './contexts/CustomerHubContext'
import {
  initializeAnalytics,
  type CustomerHubAnalyticsConfig,
} from './lib/analytics'
import { initDatadog } from './lib/datadog'
import Index from './pages/Index'
import { configureDebugLogging } from './utils/debug'
import { normalizeEnvironment } from './utils/normalizeEnv'

export type Environment = 'tst' | 'acc' | 'prd'

export type EnvironmentWithChina = Environment | 'cn_tst' | 'cn_prd'

export interface AppProps {
  env: EnvironmentWithChina
  role?: Role
  context?: string
  authToken?: string
  accountNumber?: string
  customerProfileEmail?: string
  analyticsConfig?: CustomerHubAnalyticsConfig
  /** Renders the customer's size passports. Without it the tab is not offered. */
  SizePassports?: ComponentType<SizePassportsProps>
}

/**
 * Customer Hub Application
 *
 * @example
 * ```tsx
 * import { App } from '@susu/customer-hub';
 *
 * function MyApp() {
 *   return <App env="prd" role="style-advisor" context="predict-spring" />;
 * }
 * ```
 */
export default function App({
  env,
  role,
  context,
  authToken,
  accountNumber,
  customerProfileEmail,
  analyticsConfig,
  SizePassports,
}: AppProps) {
  const datadogCleanupRef = useRef<(() => void) | null>(null)

  // useLayoutEffect in a parent fires after all children's useLayoutEffects
  // but before any child's useEffect. This guarantees analytics is initialised
  // before child tracking calls fire, without performing side effects in render.
  // Both functions are idempotent so double-invocation in concurrent mode is safe.
  useLayoutEffect(() => {
    const baseEnv = normalizeEnvironment(env)
    configureDebugLogging(env)
    initializeAnalytics(baseEnv, analyticsConfig)

    let cancelled = false

    initDatadog(baseEnv)
      .then((cleanup) => {
        if (cancelled) {
          cleanup?.()
          return
        }
        datadogCleanupRef.current = cleanup ?? null
      })
      .catch((error) => {
        console.error('Failed to initialize Datadog RUM:', error)
      })

    return () => {
      cancelled = true
      datadogCleanupRef.current?.()
      datadogCleanupRef.current = null
    }
  }, [analyticsConfig, env])

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/:area?/:orderId?"
          element={
            <Index
              env={env}
              role={role}
              context={context}
              authToken={authToken}
              accountNumber={accountNumber}
              customerProfileEmail={customerProfileEmail}
              analyticsConfig={analyticsConfig}
              SizePassports={SizePassports}
            />
          }
        />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      </Routes>
    </BrowserRouter>
  )
}
